import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Star, ArrowLeft, Clock, BarChart2, CheckCircle, AlertTriangle, ThumbsUp, ThumbsDown, BrainCircuit, User, Layers, Code, Volume2, BarChart3, Briefcase, Users, Palette, TrendingUp, Shield, MessageCircle, BookOpen, Calendar, DollarSign, Megaphone, Heart, GraduationCap, Scale, Info, FileTextIcon } from 'lucide-react';
import { Button } from '../components/common/Button';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { ParticleBackground } from '../components/ParticleBackground';
import { InterviewFeedback, FeedbackCategory, FeedbackPoint, SkillAssessment } from '../types/interviews';
import FeedbackModal from '../components/FeedbackModal';
import userFeedbackService from '../services/userFeedbackService';
import { useToast } from '../components/common/Toast';
import FeedbackRatingCard from '../components/interviews/FeedbackRatingCard';
import interviewService, { UserInterview } from '../services/interviewService';
import { buildLearnifieGoal, generateLearnifieLink } from '../utils/learnifie';

const InterviewFeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>(); // This is the userInterviewId i.e the id of the scheduled interview
  const pageRef = useRef<HTMLDivElement>(null);
  
  // Safely access feedback data from location state, avoiding issues with null/undefined
  const feedbackDataFromState = location.state?.feedbackData as InterviewFeedback | undefined;
  
  const [interviewDetails, setInterviewDetails] = useState<UserInterview | null>(null);
  const [, setIsLoading] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasFeedbackBeenSubmitted, setHasFeedbackBeenSubmitted] = useState(false);
  const [hasScrolledEnough, setHasScrolledEnough] = useState(false);
  const [hasCheckedFeedbackStatus, setHasCheckedFeedbackStatus] = useState(false);
  const [hoveredSkill, setHoveredSkill] = useState<number | null>(null);
  const [feedbackRating, setFeedbackRating] = useState<string | undefined>(undefined);
  const [isGeneratingLearnifieLink, setIsGeneratingLearnifieLink] = useState(false);
  const { showToast } = useToast();
  
  // Helper function to get the appropriate icon component
  const getIconComponent = (iconName: string, size: number = 16, className: string = '') => {
    console.log('[InterviewFeedbackPage] getIconComponent', iconName);
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      Code,
      BrainCircuit,
      Volume2,
      Clock,
      Layers,
      BarChart3,
      Briefcase,
      Users,
      Palette,
      TrendingUp,
      Shield,
      CheckCircle,
      Star,
      MessageCircle,
      BookOpen,
      Calendar,
      DollarSign,
      Megaphone,
      Heart,
      GraduationCap,
      Scale,
      BarChart2,
      Info
    };

    const IconComponent = iconMap[iconName] || Star; // Default to Star if icon not found
    return React.createElement(IconComponent, { size, className });
  };

  // Helper function to convert background color class to text color class
  const getColorClassFromBg = (bgColor: string) => {
    const colorMap: { [key: string]: string } = {
      'bg-blue-500': 'text-blue-500',
      'bg-purple-500': 'text-purple-500',
      'bg-green-500': 'text-green-500',
      'bg-orange-500': 'text-orange-500',
      'bg-indigo-500': 'text-indigo-500',
      'bg-cyan-500': 'text-cyan-500',
      'bg-emerald-500': 'text-emerald-500',
      'bg-pink-500': 'text-pink-500',
      'bg-rose-500': 'text-rose-500',
      'bg-yellow-500': 'text-yellow-500',
      'bg-red-500': 'text-red-500',
      'bg-teal-500': 'text-teal-500',
      'bg-gray-500': 'text-gray-500',
      'bg-violet-500': 'text-violet-500',
      'bg-green-600': 'text-green-600',
      'bg-indigo-600': 'text-indigo-600',
      'bg-orange-600': 'text-orange-600',
      'bg-red-600': 'text-red-600',
      'bg-blue-600': 'text-blue-600',
      'bg-gray-600': 'text-gray-600',
    };
    
    return colorMap[bgColor] || 'text-white';
  };

  // Helper function to create icon elements based on category name or color
  const createIconElement = (category: any) => {
    // Categories now come with icon and color already assigned from the backend
    return category;
  };
  
  // Check if user has already submitted feedback
  const checkFeedbackStatus = useCallback(async () => {
    if (!id || hasCheckedFeedbackStatus) return;
    
    try {
      const hasSubmitted = await userFeedbackService.hasFeedback();
      setHasFeedbackBeenSubmitted(hasSubmitted);
      setHasCheckedFeedbackStatus(true);
    } catch (error) {
      console.error("Error checking feedback status:", error);
      setHasCheckedFeedbackStatus(true);
    }
  }, [id, hasCheckedFeedbackStatus]);
  
  // Handle scroll events to detect when user has scrolled 75% down the page
  const handleScroll = useCallback(() => {
    if (!pageRef.current || hasFeedbackBeenSubmitted) return;
    
    const scrollHeight = pageRef.current.scrollHeight;
    const scrollTop = window.scrollY + window.innerHeight;
    const scrollPercentage = (scrollTop / scrollHeight) * 100;
    
    if (scrollPercentage >= 75 && !hasScrolledEnough) {
      setHasScrolledEnough(true);
    }
  }, [hasFeedbackBeenSubmitted, hasScrolledEnough]);
  
  // Show feedback modal when conditions are met
  useEffect(() => {
    if (hasScrolledEnough && !hasFeedbackBeenSubmitted && hasCheckedFeedbackStatus) {
      setShowFeedbackModal(true);
    }
  }, [hasScrolledEnough, hasFeedbackBeenSubmitted, hasCheckedFeedbackStatus]);
  
  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  // Check feedback status on component mount
  useEffect(() => {
    checkFeedbackStatus();
  }, [checkFeedbackStatus]);

  // Clear browser history to prevent going back to interview page
  useEffect(() => {
    // Set document title
    document.title = 'Interview Feedback | MockTrail';
    
    // Comprehensive history management to prevent back navigation
    const setupHistoryProtection = () => {
      try {
        // First, replace the current state to mark it as feedback page
        window.history.replaceState(
          { page: 'interview-feedback', preventBack: true, timestamp: Date.now() },
          'Interview Feedback',
          window.location.pathname + window.location.search
        );

        // Create multiple history barriers to prevent back navigation
        // This creates a "wall" of feedback page entries
        for (let i = 0; i < 5; i++) {
          window.history.pushState(
            { page: 'interview-feedback', preventBack: true, barrier: i, timestamp: Date.now() },
            'Interview Feedback',
            window.location.pathname + window.location.search
          );
        }

        console.log('History protection setup complete');
      } catch (error) {
        console.error('Error setting up history protection:', error);
      }
    };

    // Execute setup after component mounts
    const timeoutId = setTimeout(setupHistoryProtection, 100);

    // Handle browser back button with aggressive prevention
    const handlePopState = (event: PopStateEvent) => {
      console.log('PopState event triggered:', event.state);
      
      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();
      
      // Check if this is our feedback page state
      if (event.state?.page === 'interview-feedback') {
        // If user is trying to go back from feedback page, redirect to dashboard
        console.log('Preventing back navigation from feedback page');
        navigate('/', { replace: true });
        
        // After navigation, set up protection on dashboard
        setTimeout(() => {
          window.history.replaceState(
            { page: 'dashboard', preventBack: true, fromFeedback: true },
            'Dashboard',
            '/'
          );
        }, 100);
      } else {
        // For any other back navigation, also redirect to dashboard
        console.log('Redirecting any back navigation to dashboard');
        navigate('/', { replace: true });
      }
    };

    // Handle keyboard shortcuts (like Alt+Left Arrow)
    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent Alt+Left (back) and Alt+Right (forward)
      if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        event.stopPropagation();
        navigate('/', { replace: true });
      }
      
      // Prevent Backspace navigation (in some browsers)
      if (event.key === 'Backspace' && event.target === document.body) {
        event.preventDefault();
        navigate('/', { replace: true });
      }
    };

    // Add event listeners
    window.addEventListener('popstate', handlePopState, true); // Use capture phase
    window.addEventListener('keydown', handleKeyDown, true);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('popstate', handlePopState, true);
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [navigate]);
    
  useEffect(() => {
    setIsLoading(true);
    
    const fetchInterviewDetails = async () => {
      try {
        console.log("Fetching interview details for ID:", id);
        console.log("Feedback data from state:", feedbackDataFromState);
        
        if (feedbackDataFromState && feedbackDataFromState.interviewDetails) {
          console.log("Setting interview details from feedback data:", feedbackDataFromState.interviewDetails);
          setInterviewDetails({
            _id: id || '',
            user: '1',
            interview: {
              _id: feedbackDataFromState.interviewDetails.id || '',
              title: feedbackDataFromState.interviewDetails.title || '',
              company: feedbackDataFromState.interviewDetails.company || '',
              role: feedbackDataFromState.interviewDetails.jobRole || '',
              durationMinutes: feedbackDataFromState.interviewDetails.duration || 0,
              language: feedbackDataFromState.interviewDetails.language || '',
              description: feedbackDataFromState.interviewDetails.description || '', 
              tags: feedbackDataFromState.interviewDetails.tags || [],
              createdAt: feedbackDataFromState.interviewDetails.createdAt || '',
              updatedAt: feedbackDataFromState.interviewDetails.updatedAt || '',
            },
            status: 'completed',
            scheduledAt: feedbackDataFromState.interviewDetails.scheduledAt || '',
            createdAt: feedbackDataFromState.interviewDetails.createdAt || '',
            updatedAt: feedbackDataFromState.interviewDetails.updatedAt || '',
            minutesUsed: feedbackDataFromState.interviewDetails.minutesUsed || 0,
            language: feedbackDataFromState.interviewDetails.language || '',
            feedback: feedbackDataFromState
          });
        } else {
          console.log("No feedback data available, using minimal interview details");
          setInterviewDetails({
            _id: id || '',
            user: '1',
            interview: {
              _id: id || '',
              title: 'Interview Session',
              company: 'Practice Session',
              role: 'Software Engineer',
              durationMinutes: 30,
              language: 'English',
              description: 'Interview session',
              tags: [],
              createdAt: '',
              updatedAt: '',
            },
            status: 'completed',
            scheduledAt: '',
            createdAt: '',
            updatedAt: '',
            minutesUsed: 0,
            language: 'English',
            feedback: undefined,
          });
        }
      } catch (error) {
        console.error("Error fetching interview details:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInterviewDetails();
  }, [id, feedbackDataFromState]);
  
  // Get feedback data from route state or use mock data
  const [feedbackData] = useState<InterviewFeedback>(() => {
    try {
      console.log("[InterviewFeedbackPage] Feedback data from state:", location.state);
      
      let processedData: any = null;
      
      // 1. First try to get data from location state (primary source)
      if (location.state && typeof location.state === 'object') {
        if (location.state.feedbackData) {
          processedData = { ...location.state.feedbackData as any };
          console.log("Using feedback data from state.feedbackData:", processedData);
        } else {
          // Support legacy format without feedbackData wrapper
          processedData = { ...location.state as any };
          console.log("Using legacy feedback data format:", processedData);
        }
      }
      
      // 2. If we couldn't get data from location state, try sessionStorage as fallback
      if (!processedData) {
        try {
          const storedFeedbackData = sessionStorage.getItem('interview_feedback_data');
          if (storedFeedbackData) {
            console.log("[InterviewFeedbackPage] Found feedback data in sessionStorage");
            processedData = JSON.parse(storedFeedbackData);
            console.log("[InterviewFeedbackPage] Successfully parsed sessionStorage data:", 
              { hasCategories: !!processedData?.categories, hasStrengths: !!processedData?.strengths });
          }
        } catch (sessionStorageError) {
          console.error("Error retrieving feedback data from sessionStorage:", sessionStorageError);
        }
      }
      
      // 3. Categories already come with icons and colors assigned
      if (processedData?.categories) {
        processedData.categories = processedData.categories.map(createIconElement);
      }
      
      // 4. Only clear sessionStorage if we successfully got data from somewhere
      if (processedData) {
        try {
          // Don't immediately remove - keep as fallback for a while in case of navigation issues
          // Instead, mark it as accessed
          sessionStorage.setItem('interview_feedback_data_accessed', 'true');
          console.log("[InterviewFeedbackPage] Marked feedback data as accessed");
          
          // Schedule removal after 1 minute
          setTimeout(() => {
            if (sessionStorage.getItem('interview_feedback_data_accessed') === 'true') {
              sessionStorage.removeItem('interview_feedback_data');
              sessionStorage.removeItem('interview_feedback_data_accessed');
              console.log("[InterviewFeedbackPage] Cleared feedback data from sessionStorage after timeout");
            }
          }, 60000);
        } catch (error) {
          console.error("Error setting sessionStorage flag:", error);
        }
      }
      
      return processedData;
    } catch (error) {
      console.error("Error processing feedback data:", error);
      // Try one more time to get data from sessionStorage as emergency fallback
      try {
        const emergencyData = sessionStorage.getItem('interview_feedback_data');
        if (emergencyData) {
          console.log("[InterviewFeedbackPage] Emergency recovery of feedback data from sessionStorage");
          return JSON.parse(emergencyData);
        }
      } catch (e) {
        console.error("Final attempt to recover data failed:", e);
      }
      
      // Return undefined if all recovery methods fail
      return undefined;
    }
  });

  const handleGenerateLearnifieLink = async () => {
    if (isGeneratingLearnifieLink) {
      return;
    }

    const goalPayload = buildLearnifieGoal({
      feedback: feedbackData,
      userInterview: interviewDetails
    });

    if (!goalPayload) {
      showToast('Missing interview feedback to send to Learnifie. Please refresh and try again.', 'error');
      return;
    }

    setIsGeneratingLearnifieLink(true);

    try {
      const data = await generateLearnifieLink(goalPayload);

      window.open(data.url, '_blank', 'noopener,noreferrer');
      showToast('Opened your Learnifie improvement plan in a new tab.', 'success');
    } catch (error) {
      console.error('Error generating Learnifie link:', error);
      window.open('https://www.learnifie.com?utm_source=mocktrail&utm_medium=interview_feedback_page_learnifie_link', '_blank', 'noopener,noreferrer');
    } finally {
      setIsGeneratingLearnifieLink(false);
    }
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Today";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  
  // Format duration as hh:mm:ss like if secondsUsed is 127, it should be 2m 7s
  const formatDuration = (secondsUsed: number) => {
    console.log("[InterviewFeedbackPage] Seconds used:", secondsUsed);
    if (secondsUsed <= 60) return '<1m';
    
    const hours = Math.floor(secondsUsed / 3600);
    const minutes = Math.floor((secondsUsed % 3600) / 60);
    const seconds = secondsUsed % 60;
    
    console.log("hours:", hours);
    console.log("minutes:", minutes);
    console.log("seconds:", seconds);
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    parts.push(`${minutes}m ${seconds}s`);
    
    return parts.join(' ');
  };
  
  // Determine score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  // Determine score background
  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Return verbal assessment based on score
  const getScoreVerbal = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Satisfactory';
    if (score >= 50) return 'Average';
    return 'Poor';
  };

  // Return impression verbal rating
  const getImpressionVerbal = (rating: number) => {
    if (rating >= 5) return 'Exceptional';
    if (rating >= 4) return 'Impressive';
    if (rating >= 3) return 'Satisfactory';
    if (rating >= 2) return 'Below Average';
    return 'Average';
  };
  
  const handleReturnToDashboard = () => {
    navigate('/', { replace: true });
  };
  
  const handleViewMyInterviews = () => {
    // Navigate to interviews list page
    navigate('/my-interviews', { replace: true });
  };
  
  const handleFeedbackSubmitted = async () => {
    showToast('Thank you for your feedback!', 'success');
    setShowFeedbackModal(false);
    setHasFeedbackBeenSubmitted(true);
  };
  
  const handleSkipFeedback = () => {
    setShowFeedbackModal(false);
    setHasFeedbackBeenSubmitted(true);
  };

  // Handle feedback rating submission
  const handleFeedbackRatingSubmit = async (rating: 'like' | 'dislike') => {
    if (!id) return;
    
    try {
      // Update the user interview with the feedback rating
      const response = await interviewService.updateUserFeedbackRating(id, rating as 'like' | 'dislike');

      if (response) {
        setFeedbackRating(rating);
      } else {
        throw new Error('Failed to submit feedback rating');
      }
    } catch (error) {
      console.error('Error submitting feedback rating:', error);
      throw error;
    }
  };
  
  // Custom background gradient styles to match InterviewHistoryPage
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };
  
  return (
    <div 
      className="min-h-screen bg-background-light dark:bg-background-dark" 
      style={backgroundGradient}
      ref={pageRef}
    >
      {/* Animated gradient elements from InterviewHistoryPage */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <div className="container mx-auto max-w-7xl px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          
          <PageTransitionItem index={1}>
            <div className="mb-6 mt-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                Interview Feedback
              </h1>
              <p className="mt-2 text-base text-text-secondary-light dark:text-text-secondary-dark">
                {interviewDetails?.interview?.title} • {formatDate(interviewDetails?.scheduledAt || '')}
              </p>
            </div>
          </PageTransitionItem>
          
             {/* If no feedback exists, show a message */}
             {(!feedbackData || (feedbackData && !feedbackData.categories && !feedbackData.summary)) ? (
               <div className="flex justify-center items-center text-center p-2 pt-20 md:pt-34 h-full">
               <div className="flex flex-col items-center gap-4">
                 <FileTextIcon className="h-12 w-12 text-text-tertiary-light dark:text-text-tertiary-dark" />
                 <div>
                   <h3 className="text-lg md:text-xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                     No feedback available
                   </h3>
                   <p className="text-sm md:text-base text-text-secondary-light dark:text-text-secondary-dark max-w-2xl">
                     We didn't find a significant amount of time spent on this interview, so no valuable feedback was generated.
                     <br />Your Interview will be marked as completed, but you can try again later.
                   </p>
                   <div className="mt-4 flex justify-center">
                     <Button
                       onClick={() => navigate('/', { replace: true })}
                       className="px-4 py-2"
                       variant="outline"
                     >
                       Back to Dashboard
                     </Button>
                   </div>
                 </div>
               </div>
             </div>
            ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left column - Summary and scores */}
            <div className="lg:col-span-3">
              <PageTransitionItem index={2}>
                {/* Interview details */}
                <div className="p-4 md:p-6 mb-6 bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
                  {/* Interview Info */}
                  <div className="mb-6 bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Job Role</p>
                        <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{feedbackData.interviewDetails?.jobRole || 'Interview'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Company</p>
                        <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{feedbackData.interviewDetails?.company || 'Practice Session'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Duration (Used)</p>
                        <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{formatDuration(feedbackData.interviewDetails?.minutesUsed || 0)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Overall Score with circular progress */}
                  <div className="mb-8">
                    <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                      <BarChart2 size={18} className="mr-2 text-text-primary-light dark:text-text-primary-dark" />
                      Overall Performance
                    </h4>
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="flex items-center justify-center">
                        <div className="relative w-32 h-32 flex items-center justify-center">
                          <svg className="w-full h-full" viewBox="0 0 100 100">
                            <circle
                              className="text-background-light dark:text-background-dark stroke-current"
                              strokeWidth="10"
                              cx="50"
                              cy="50"
                              r="40"
                              fill="transparent"
                            ></circle>
                            <circle
                              className={`${getScoreColor(feedbackData.overallScore || 0)} stroke-current`}
                              strokeWidth="10"
                              strokeLinecap="round"
                              cx="50"
                              cy="50"
                              r="40"
                              fill="transparent"
                              strokeDasharray={`${2.5 * Math.PI * 40 * (feedbackData.overallScore || 0) / 100} ${
                                2.5 * Math.PI * 40 * (1 - (feedbackData.overallScore || 0) / 100)
                              }`}
                              strokeDashoffset={2.5 * Math.PI * 10}
                            ></circle>
                          </svg>
                          <div className="absolute flex flex-col items-center justify-center">
                            <span className={`text-2xl font-bold ${getScoreColor(feedbackData.overallScore || 0)}`}>
                              {feedbackData.overallScore || 0}%
                            </span>
                            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                              {getScoreVerbal(feedbackData.overallScore || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                        <p className="text-text-secondary-light dark:text-text-secondary-dark mb-3 text-sm">
                          {feedbackData.summary}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category Scores with progress bars */}
                  <div className="mb-8">
                    <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                      <BrainCircuit size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
                      Performance by Category
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {feedbackData.categories?.map((category: FeedbackCategory, index: number) => (
                        <div 
                          key={index} 
                          className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className={`p-1.5 rounded-full mr-2 bg-opacity-20 dark:bg-opacity-20 ${category.color || 'bg-gray-500'}`}>
                                {category.icon && getIconComponent(category.icon, 16, getColorClassFromBg(category.color || 'bg-gray-500'))}
                              </div>
                              <h5 className="font-medium text-text-primary-light dark:text-text-primary-dark">{category.name}</h5>
                            </div>
                            <span className={`font-bold ${getScoreColor(category.score)}`}>
                              {category.score}%
                            </span>
                          </div>
                          <div className="w-full h-2 bg-background-light dark:bg-background-dark rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${getScoreBg(category.score)}`}
                              style={{ width: `${category.score}%` }}
                            ></div>
                          </div>
                          <p className="mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            {getScoreVerbal(category.score)}
                          </p>
                          <p className="mt-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            {category.feedback}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Skills Assessment */}
                  {feedbackData.skills && feedbackData.skills?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                        <Star size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
                        Skills Covered in Interview
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {feedbackData.skills?.map((skill: SkillAssessment, index: number) => (
                          <div 
                            key={index} 
                            className="relative"
                            onMouseEnter={() => setHoveredSkill(index)}
                            onMouseLeave={() => setHoveredSkill(null)}
                          >
                            <div className="inline-flex items-center bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-full px-4 py-2 gap-2 min-w-fit">
                              <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark whitespace-nowrap">
                                {skill.name}
                              </span>
                              <span className="text-xs font-semibold text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 px-2 py-0.5 rounded-full">
                                {skill.score}/5
                              </span>
                              <Info 
                                size={14} 
                                className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark cursor-help transition-colors" 
                              />
                            </div>
                            
                            {/* Tooltip */}
                            <div 
                              className={`absolute z-10 -bottom-16 left-1/2 transform -translate-x-1/2 w-64
                                bg-surface-light dark:bg-surface-dark 
                                text-text-primary-light dark:text-text-primary-dark px-3 py-2 rounded-lg text-xs
                                border border-border-light dark:border-border-dark shadow-md transition-opacity duration-200
                                ${hoveredSkill === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                              `}
                            >
                              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 
                                bg-surface-light dark:bg-surface-dark border-t border-l border-border-light dark:border-border-dark"></div>
                              <div className="text-center break-normal">
                                {skill.feedback}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Interviewer's Impression */}
                  {feedbackData.impression && feedbackData.impression.rating && (
                    <div className="mb-8">
                      <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                        <User size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
                        Interviewer's Impression
                      </h4>
                      <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                          <div className="flex items-center">
                            <div className="bg-primary-light/10 dark:bg-primary-dark/10 rounded-full p-2 mr-3">
                              <div className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
                                {feedbackData.impression.rating || 0}/5
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                Overall Impression
                              </p>
                              <p className="text-base font-medium text-text-primary-light dark:text-text-primary-dark font-heading">
                                {getImpressionVerbal(feedbackData.impression.rating || 0)}
                              </p>
                            </div>
                          </div>
                          <div className="flex-1 border-l-0 md:border-l border-border-light dark:border-border-dark md:pl-4">
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                              Interviewer's Notes
                            </p>
                            <p className="text-base text-text-primary-light dark:text-text-primary-dark">
                              {feedbackData.impression.comment}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Strengths section */}
                  {feedbackData.strengths && feedbackData.strengths?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                        <ThumbsUp size={18} className="mr-2 text-green-500" />
                        What You Did Well
                      </h4>
                      <div className="space-y-4">
                        {feedbackData.strengths?.map((strength: FeedbackPoint, index: number) => (
                          <div 
                            key={index} 
                            className="bg-surface-light dark:bg-surface-dark border border-green-500/20 dark:border-green-500/20 p-4 rounded-lg"
                          >
                            <div className="flex items-start">
                              <CheckCircle size={18} className="text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                              <div>
                                <p className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2 font-heading">
                                  {strength.text}
                                </p>
                                {strength.reason && (
                                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strength.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Areas for improvement section */}
                  {feedbackData.improvements && feedbackData.improvements?.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                        <ThumbsDown size={18} className="mr-2 text-amber-500" />
                        Areas for Improvement
                      </h4>
                      <div className="space-y-4">
                        {feedbackData.improvements?.map((improvement: FeedbackPoint, index: number) => (
                          <div 
                            key={index} 
                            className="bg-surface-light dark:bg-surface-dark border border-yellow-500/20 dark:border-yellow-500/20 p-4 rounded-lg"
                          >
                            <div className="flex items-start">
                              <AlertTriangle size={18} className="text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                              <div>
                                <p className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2 font-heading">
                                  {improvement.text}
                                </p>
                                {improvement.reason && (
                                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {improvement.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PageTransitionItem>
            </div>
            
            {/* Right column - Actions and Feedback Rating */}
            <div className="lg:col-span-1">
              <PageTransitionItem index={3}>
                <div className="space-y-4">
                  {/* Learnifie Improvement Card */}
                  <div className="bg-background-light dark:bg-background-dark border border-primary-light/30 dark:border-primary-dark/30 rounded-xl p-4 md:p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-heading font-bold text-primary-light dark:text-primary-dark mb-2">
                          Improve with Learnifie
                        </h3>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Turn this <span className='text-text-primary-light dark:text-text-primary-dark font-medium'>feedback into a personalised learning</span> plan tailored to your interview performance.
                        </p>
                      </div>
                      <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-white/40 dark:bg-black/20 text-primary-light dark:text-primary-dark">
                        <GraduationCap className="w-6 h-6" />
                      </div>
                    </div>
                    <Button
                      className="mt-4 py-3"
                      size="sm"
                      fullWidth
                      variant="primary"
                      loading={isGeneratingLearnifieLink}
                      onClick={handleGenerateLearnifieLink}
                      leftIcon={<GraduationCap className="w-4 h-4" />}
                    >
                      {isGeneratingLearnifieLink ? 'Generating...' : 'Generate Curriculum'}
                    </Button>
                  </div>

                  {/* Feedback Rating Card */}
                  <FeedbackRatingCard
                    interviewId={id || ''}
                    currentRating={feedbackRating}
                    onRatingSubmit={handleFeedbackRatingSubmit}
                  />
                  
                  {/* Actions Card */}
                  <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
                    <h3 className="text-lg font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
                      Actions
                    </h3>
                    <div className="space-y-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        leftIcon={<ArrowLeft className="w-4 h-4" />}
                        onClick={handleReturnToDashboard}
                        className="w-full justify-start"
                      >
                        Back to Dashboard
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        leftIcon={<Layers className="w-4 h-4" />}
                        onClick={handleViewMyInterviews}
                        className="w-full justify-start"
                      >
                        View My Interviews
                      </Button>
                    </div>
                  </div>
                </div>
              </PageTransitionItem>
            </div>
          </div>
        )}
        </div>
      </div>
      
      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)}
        onFeedbackSubmitted={handleFeedbackSubmitted}
        onSkip={handleSkipFeedback}
      />
    </div>
  );
};

InterviewFeedbackPage.displayName = 'InterviewFeedbackPage';

export default withPageTransition(InterviewFeedbackPage); 
